import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Divider, Form, Input, message, Row,
  Select, Space, Statistic, Switch, Table, Tag, Typography
} from 'antd';
import {
  CheckCircleTwoTone, ExclamationCircleTwoTone, SyncOutlined,
  ThunderboltOutlined, SettingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { ErpMasterShell } from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import './accounts.css';

const { Title, Paragraph, Text } = Typography;

// Stage 6B.2 — Accounts Setup page.
// Single screen that drives the entire accounting-readiness checklist.
// Sections:
//   1. Readiness card  (live status from /setup/status)
//   2. Financial year + period generator
//   3. GST setup       (default GSTIN / state code / filing scheme)
//   4. Document sequences (link + ensure-button)
//   5. Opening balances (link)
//   6. Next recommended steps (server-supplied nextActions[])
//
// Intentionally compact — no transactional forms here; transactional
// UI ships in a later stage (sales invoice, purchase bill, payment).

function StatusBadge({ ok, label }) {
  return ok
    ? <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginInlineEnd: 0 }}>{label}</Tag>
    : <Tag color="warning" icon={<ExclamationCircleTwoTone twoToneColor="#faad14" />} style={{ marginInlineEnd: 0 }}>{label}</Tag>;
}

function ReadinessCard({ status, loading, onRefresh }) {
  if (loading && !status) {
    return <Card loading title="Setup Readiness" />;
  }
  if (!status) return null;
  return (
    <Card
      title={<><SettingOutlined style={{ marginInlineEnd: 8 }} />Setup Readiness</>}
      extra={<Button size="small" icon={<SyncOutlined />} onClick={onRefresh}>Refresh</Button>}
    >
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={8} md={6}>
          <Statistic title="Current FY" value={status.currentFinancialYear || '—'} />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Statistic title="Open Periods"    value={status.openPeriodsCount} />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Statistic title="Locked Periods"  value={status.lockedPeriodsCount} />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Statistic title="Closed Periods"  value={status.closedPeriodsCount} />
        </Col>
      </Row>
      <Divider style={{ margin: '12px 0' }} />
      <Space size={[8, 8]} wrap>
        <StatusBadge ok={status.financialYearConfigured} label="Periods × 12" />
        <StatusBadge ok={status.systemLedgersReady}      label="System Ledgers" />
        <StatusBadge ok={status.documentSequencesReady}  label="Doc Sequences" />
        <StatusBadge ok={status.taxRatesReady}           label="Tax Rates" />
        <StatusBadge ok={status.gstSetupReady}           label="GST Views" />
        <StatusBadge ok={status.openingBalancesEntered}  label="Opening Balances" />
        <StatusBadge ok={status.setupCompleted}          label="Setup Marked Complete" />
      </Space>
      {Array.isArray(status.warnings) && status.warnings.length > 0 ? (
        <Alert
          type="warning" showIcon banner style={{ marginTop: 12 }}
          message="Warnings"
          description={
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {status.warnings.map((w, i) => (<li key={i}>{w}</li>))}
            </ul>
          }
        />
      ) : null}
    </Card>
  );
}

function PeriodGeneratorCard({ defaultFy, onGenerated }) {
  const [fy, setFy] = useState(defaultFy || '2026-27');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (defaultFy) setFy(defaultFy); }, [defaultFy]);
  const generate = useCallback(async (kind) => {
    setBusy(true);
    try {
      const data = kind === 'quarter'
        ? await accountsApi.generateQuarters({ financialYear: fy })
        : await accountsApi.generatePeriods({ financialYear: fy });
      message.success(`Generated ${data.createdCount} ${kind === 'quarter' ? 'quarter(s)' : 'month(s)'} for FY ${data.financialYear} (skipped ${data.skippedCount}).`);
      onGenerated && onGenerated(data);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e.message || 'Failed to generate periods.';
      message.error(msg);
    } finally { setBusy(false); }
  }, [fy, onGenerated]);
  return (
    <Card title="Financial Year & Periods">
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Indian fiscal year runs <Text strong>April 1 → March 31</Text>. Generation is
        idempotent — rerunning the same FY skips months that already exist and never
        overwrites locked or closed periods.
      </Paragraph>
      <Space wrap>
        <Input
          value={fy}
          onChange={(e) => setFy(e.target.value.trim())}
          placeholder="2026-27"
          style={{ width: 120 }}
          maxLength={9}
        />
        <Button type="primary" icon={<ThunderboltOutlined />} loading={busy}
                onClick={() => generate('month')}>
          Generate Monthly Periods
        </Button>
        <Button onClick={() => generate('quarter')} loading={busy}>
          Generate Quarterly Periods
        </Button>
        <Link to="/erp/accounts/periods">Open Periods page →</Link>
      </Space>
    </Card>
  );
}

const FILING_SCHEMES = [
  { value: 'monthly',         label: 'Monthly' },
  { value: 'quarterly_qrmp',  label: 'Quarterly QRMP' }
];

function GstSetupCard({ settings, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    form.setFieldsValue({
      currentFinancialYear:           settings?.currentFinancialYear || '',
      booksStartDate:                 settings?.booksStartDate ? dayjs(settings.booksStartDate) : null,
      defaultGstin:                   settings?.defaultGstin || '',
      defaultGstStateCode:            settings?.defaultGstStateCode || '',
      defaultPlaceOfSupplyStateCode:  settings?.defaultPlaceOfSupplyStateCode || '',
      filingScheme:                   settings?.filingScheme || 'monthly',
      qrmpEnabled:                    settings?.qrmpEnabled === true,
      notes:                          settings?.notes || ''
    });
  }, [settings, form]);

  const onSave = useCallback(async (values) => {
    setBusy(true);
    try {
      const payload = {
        currentFinancialYear:           values.currentFinancialYear || null,
        booksStartDate:                 values.booksStartDate ? values.booksStartDate.format('YYYY-MM-DD') : null,
        defaultGstin:                   (values.defaultGstin || '').toUpperCase() || null,
        defaultGstStateCode:            values.defaultGstStateCode || null,
        defaultPlaceOfSupplyStateCode:  values.defaultPlaceOfSupplyStateCode || null,
        filingScheme:                   values.filingScheme || 'monthly',
        qrmpEnabled:                    values.qrmpEnabled === true,
        notes:                          values.notes || null
      };
      const data = await accountsApi.updateSettings(payload);
      message.success('GST setup saved.');
      onSaved && onSaved(data);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e.message || 'Failed to save GST setup.';
      message.error(msg);
    } finally { setBusy(false); }
  }, [onSaved]);

  return (
    <Card title="GST Setup">
      <Alert
        type="info" showIcon banner style={{ marginBottom: 12 }}
        message="GST reports are draft review outputs until connected to GSP / GST filing services in a later stage."
      />
      <Form form={form} layout="vertical" onFinish={onSave} preserve>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Current Financial Year" name="currentFinancialYear">
              <Input maxLength={9} placeholder="2026-27" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Books Start Date" name="booksStartDate">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Filing Scheme" name="filingScheme">
              <Select options={FILING_SCHEMES} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Default GSTIN" name="defaultGstin">
              <Input maxLength={15} placeholder="27AAAAA0000A1Z5" />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="Default State" name="defaultGstStateCode">
              <Input maxLength={2} placeholder="27" />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="Default POS State" name="defaultPlaceOfSupplyStateCode">
              <Input maxLength={2} placeholder="27" />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="QRMP Enabled" name="qrmpEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Notes" name="notes">
              <Input.TextArea rows={2} maxLength={2000} />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button type="primary" htmlType="submit" loading={busy}>Save GST Setup</Button>
          <Text type="secondary">
            Default GSTIN / state drive intra/inter-state classification when a transaction omits them.
          </Text>
        </Space>
      </Form>
    </Card>
  );
}

function RequiredSequencesCard({ status, onChanged }) {
  const [busy, setBusy] = useState(false);
  const required = status?.requiredDocumentSequences || [];
  const missing  = new Set(status?.missingDocumentSequences || []);
  const rows = required.map((dt) => ({
    documentType: dt,
    present: !missing.has(dt)
  }));
  const ensure = useCallback(async () => {
    setBusy(true);
    try {
      const data = await accountsApi.ensureDocumentSequences({
        financialYear: status?.currentFinancialYear
      });
      message.success(`Created ${data.createdCount} document sequence(s) for FY ${data.financialYear}.`);
      onChanged && onChanged();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to ensure sequences.');
    } finally { setBusy(false); }
  }, [status, onChanged]);
  return (
    <Card title="Document Sequences"
      extra={
        <Space>
          <Button size="small" loading={busy} onClick={ensure} disabled={missing.size === 0}>
            Create Missing
          </Button>
          <Link to="/erp/accounts/document-sequences">Open Sequences →</Link>
        </Space>
      }>
      <Table
        size="small"
        rowKey="documentType"
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'Document Type', dataIndex: 'documentType', width: 220 },
          { title: 'Status',        dataIndex: 'present', width: 120, align: 'center',
            render: (v) => v
              ? <Tag color="success" style={{ marginInlineEnd: 0 }}>Configured</Tag>
              : <Tag color="warning" style={{ marginInlineEnd: 0 }}>Missing</Tag> }
        ]}
      />
    </Card>
  );
}

function OpeningBalancesCard({ status }) {
  return (
    <Card title="Opening Balances"
      extra={<Link to="/erp/accounts/opening-balances">Open Opening Balances →</Link>}>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Enter starting trial-balance amounts per ledger. The system tracks total debit
        vs total credit and flags any imbalance. Confirming opening balances flips the
        readiness gate; the posting engine (later stage) will read this flag before
        accepting business-day transactions.
      </Paragraph>
      <Space wrap>
        <StatusBadge ok={status?.openingBalancesEntered}    label="Some balances entered" />
        <StatusBadge ok={status?.openingBalancesConfirmed}  label="Confirmed" />
      </Space>
    </Card>
  );
}

function NextActionsCard({ status }) {
  const actions = (status && status.nextActions) || [];
  if (!actions.length) {
    return (
      <Card title="Next Recommended Steps">
        <Alert type="success" showIcon banner
          message="All foundation gates met — you're ready for the posting engine stage." />
      </Card>
    );
  }
  return (
    <Card title="Next Recommended Steps">
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
        {actions.map((a, i) => (
          <li key={i}>
            <Text strong>{a.message}</Text>
            {a.endpoint ? <Text type="secondary"> &nbsp;<code>{a.endpoint}</code></Text> : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function AccountsSetupPage() {
  const [status, setStatus]     = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        accountsApi.getSetupStatus(),
        accountsApi.getSettings()
      ]);
      setStatus(s);
      setSettings(st);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load setup status.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const defaultFy = useMemo(
    () => status?.currentFinancialYear || '2026-27',
    [status]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Accounts Setup</Title>
        <Alert
          type="warning" showIcon banner style={{ marginBottom: 10 }}
          message="Foundation only"
          description="This screen captures setup preferences and shows the readiness checklist. Full Sales / Purchase / Inventory forms and live GST portal submission are out of scope for this stage."
        />
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <ReadinessCard status={status} loading={loading} onRefresh={refresh} />
          </Col>
          <Col xs={24} lg={12}>
            <PeriodGeneratorCard defaultFy={defaultFy} onGenerated={refresh} />
          </Col>
          <Col xs={24} lg={12}>
            <RequiredSequencesCard status={status} onChanged={refresh} />
          </Col>
          <Col xs={24}>
            <GstSetupCard settings={settings} onSaved={(s) => { setSettings(s); refresh(); }} />
          </Col>
          <Col xs={24} lg={12}>
            <OpeningBalancesCard status={status} />
          </Col>
          <Col xs={24} lg={12}>
            <NextActionsCard status={status} />
          </Col>
        </Row>
      </div>
    </ErpMasterShell>
  );
}
